import { useQuery } from '@tanstack/react-query'
import { Globe, TrendingUp, MousePointer, Users } from 'lucide-react'
import { seoAnalytics } from '@/lib/api'

interface TrafficOverviewCardsProps {
  days: number
  section?: string
}

const SOURCE_LABELS: Record<string, string> = {
  google_organic: 'Google Organico',
  bing_organic: 'Bing Organico',
  google_ads: 'Google Ads',
  direct: 'Trafico Directo',
  referral: 'Referencias',
  other: 'Otros',
}

const SOURCE_COLORS: Record<string, string> = {
  google_organic: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  bing_organic: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  google_ads: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  direct: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-400',
  referral: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  other: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
}

export default function TrafficOverviewCards({ days, section }: TrafficOverviewCardsProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['seo-traffic-overview', days, section],
    queryFn: () => seoAnalytics.getTrafficOverview(days, section),
  })

  const overview = data?.data

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="card p-4 animate-pulse">
            <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-20 mb-2" />
            <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded w-16" />
          </div>
        ))}
      </div>
    )
  }

  const totalVisits = overview?.totals?.total_visits || 0
  const totalUniqueVisitors = overview?.totals?.total_visitors || 0
  const bySource = overview?.by_source || []

  // Calculate percentages using unique visitors (not HTTP requests)
  const organicVisitors = bySource
    .filter((s: any) => s.source?.includes('organic'))
    .reduce((sum: number, s: any) => sum + (s.unique_visitors || 0), 0)
  const adsVisitors = bySource
    .filter((s: any) => s.source?.includes('ads'))
    .reduce((sum: number, s: any) => sum + (s.unique_visitors || 0), 0)

  const organicPercent = totalUniqueVisitors > 0 ? Math.round((organicVisitors / totalUniqueVisitors) * 100) : 0
  const adsPercent = totalUniqueVisitors > 0 ? Math.round((adsVisitors / totalUniqueVisitors) * 100) : 0

  return (
    <div className="space-y-4">
      {/* Main metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-1">
            <Globe className="w-4 h-4" />
            <span className="text-sm">Visitas Totales</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {totalVisits.toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">Ultimos {days} dias</div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-1">
            <Users className="w-4 h-4" />
            <span className="text-sm">Visitantes Unicos</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {totalUniqueVisitors.toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1">Por IP unica</div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm">Trafico Organico</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {organicPercent}%
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {organicVisitors.toLocaleString()} visitantes
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-1">
            <MousePointer className="w-4 h-4" />
            <span className="text-sm">Google Ads</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 dark:text-white">
            {adsPercent}%
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {adsVisitors.toLocaleString()} visitantes
          </div>
        </div>
      </div>

      {/* Source breakdown */}
      <div className="card p-4">
        <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
          Desglose por Fuente (Visitantes Unicos)
        </h3>
        <div className="flex flex-wrap gap-2">
          {bySource.map((source: any) => {
            const visitors = source.unique_visitors || 0
            const percent = totalUniqueVisitors > 0
              ? Math.round((visitors / totalUniqueVisitors) * 100)
              : 0
            const colorClass = SOURCE_COLORS[source.source] || SOURCE_COLORS.other
            return (
              <div
                key={source.source}
                className={`px-3 py-1.5 rounded-lg text-sm ${colorClass}`}
              >
                <span className="font-medium">
                  {SOURCE_LABELS[source.source] || source.source}
                </span>
                <span className="ml-2 opacity-75">
                  {visitors.toLocaleString()} ({percent}%)
                </span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
